/**
 * Phase 7-B — Operator earnings dashboard.
 *
 * Surfaces `GET /operators/me/earnings` (`02_api_spec.md §4.2`):
 *   4 KPIs · inline SVG line chart (no chart lib) · top capabilities · recent
 *   attestations.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import Badge from '../../components/Badge';
import { weiToAdi, shortAddr } from './utils';
import type { MyEarnings } from '@infinia/ida-sdk';

export default function Earnings() {
  const { t } = useTranslation(['marketplace', 'common']);
  const [data, setData] = useState<MyEarnings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch<MyEarnings>('/operators/me/earnings?rangeDays=30');
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('earnings.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [t]);

  if (loading) {
    return <div className="text-center py-20 text-sm text-text-faint">{t('earnings.loading')}</div>;
  }
  if (error || !data) {
    return <div className="text-center py-20 text-sm text-red-500">{error || t('earnings.loadFailed')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('earnings.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('earnings.subtitle')}</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon="inventory_2" label={t('earnings.kpis.totalListings')} value={String(data.totals?.totalListings ?? 0)} />
        <Kpi icon="lock_clock" label={t('earnings.kpis.activeEscrows')} value={String(data.totals?.activeEscrows ?? 0)} />
        <Kpi icon="check_circle" label={t('earnings.kpis.completedEscrows')} value={String(data.totals?.completedEscrows ?? 0)} />
        <Kpi icon="paid" label={t('earnings.kpis.lifetimeAdi')} value={`${weiToAdi(data.totals?.totalEarningsWei ?? '0')} ADI`} />
      </div>

      {/* Chart + Top capabilities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-xl border border-border-subtle bg-white p-5">
          <h3 className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-text-faint">
            {t('earnings.chart.title')}
          </h3>
          <EarningsLineChart points={data.timeseries ?? []} />
        </div>

        <div className="rounded-xl border border-border-subtle bg-white p-5">
          <h3 className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-text-faint">
            {t('earnings.topCapabilities.title')}
          </h3>
          {(data.topCapabilities ?? []).length === 0 ? (
            <p className="text-xs text-text-faint">{t('earnings.topCapabilities.empty')}</p>
          ) : (
            <ul className="space-y-3">
              {(data.topCapabilities ?? []).map((c) => (
                <li key={c.capabilityScope} className="rounded-lg border border-border-subtle p-3">
                  <p className="font-mono text-xs font-bold text-text-main truncate">{c.capabilityScope}</p>
                  <div className="mt-1 grid grid-cols-3 gap-2 text-[10px] text-text-muted">
                    <span>{t('earnings.topCapabilities.deliveries')}: <b className="text-text-main">{c.deliveries}</b></span>
                    <span>{t('earnings.topCapabilities.earnings')}: <b className="text-text-main">{weiToAdi(c.earningsWei)}</b></span>
                    <span>{t('earnings.topCapabilities.avgScore')}: <b className="text-text-main">{c.avgScore.toFixed(1)}</b></span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Pending payouts + badges */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Kpi icon="hourglass_empty" label={t('earnings.kpis.pendingPayouts')} value={`${weiToAdi(data.totals?.pendingPayoutsWei ?? '0')} ADI`} />
        <Kpi icon="workspace_premium" label={t('earnings.kpis.badges')} value={String(data.totals?.reputationBadgeCount ?? 0)} />
      </div>

      {/* Recent attestations */}
      <div className="rounded-xl border border-border-subtle bg-white p-5">
        <h3 className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-text-faint">
          {t('earnings.attestations.title')}
        </h3>
        {(data.recentAttestations ?? []).length === 0 ? (
          <p className="text-xs text-text-faint">{t('earnings.attestations.empty')}</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {(data.recentAttestations ?? []).map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant={a.scoreBucket === 'positive' ? 'success' : a.scoreBucket === 'neutral' ? 'warning' : 'error'}>
                      {a.score}
                    </Badge>
                    <span className="font-mono text-[10px] text-text-faint">{shortAddr(a.verifierDid, 6)}</span>
                  </div>
                  {a.rationale && <p className="mt-1 line-clamp-1 text-xs text-text-muted">{a.rationale}</p>}
                </div>
                <span className="text-[10px] text-text-faint shrink-0">{new Date(a.createdAt).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-white p-4">
      <div className="mb-1 flex items-center gap-1.5 text-text-faint">
        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>
        <span className="text-[10px] uppercase tracking-wide font-bold">{label}</span>
      </div>
      <p className="text-xl font-black text-text-main truncate">{value}</p>
    </div>
  );
}

interface Point { date: string; earningsWei: string; deliveries: number }

function EarningsLineChart({ points }: { points: Point[] }) {
  const { t } = useTranslation('marketplace');

  const series = useMemo(() => {
    return points.map((p) => {
      let v = 0;
      try { v = Number(BigInt(p.earningsWei) / 10n ** 14n) / 10000; } catch { v = 0; }
      return { date: p.date, value: v };
    });
  }, [points]);

  if (series.length === 0) {
    return <p className="py-12 text-center text-xs text-text-faint">{t('earnings.chart.empty')}</p>;
  }

  const W = 600;
  const H = 180;
  const PAD = 24;
  const max = Math.max(0.0001, ...series.map((s) => s.value));
  const stepX = series.length > 1 ? (W - PAD * 2) / (series.length - 1) : 0;

  const path = series
    .map((s, i) => {
      const x = PAD + i * stepX;
      const y = H - PAD - (s.value / max) * (H - PAD * 2);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  // Gridlines (4 horizontal).
  const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => H - PAD - f * (H - PAD * 2));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
      {grid.map((y, i) => (
        <line key={i} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray={i === 4 ? undefined : '2 4'} />
      ))}
      <path d={path} fill="none" stroke="#3b82f6" strokeWidth="2" />
      {series.map((s, i) => {
        const x = PAD + i * stepX;
        const y = H - PAD - (s.value / max) * (H - PAD * 2);
        return <circle key={i} cx={x} cy={y} r="2" fill="#3b82f6" />;
      })}
      {/* Y-axis labels (max + 0) */}
      <text x={4} y={PAD + 4} fontSize="10" fill="#94a3b8" fontFamily="monospace">{max.toFixed(2)}</text>
      <text x={4} y={H - PAD + 4} fontSize="10" fill="#94a3b8" fontFamily="monospace">0</text>
      {/* X-axis labels (first + last) */}
      <text x={PAD} y={H - 4} fontSize="9" fill="#94a3b8" fontFamily="monospace">{series[0].date}</text>
      <text x={W - PAD} y={H - 4} fontSize="9" fill="#94a3b8" fontFamily="monospace" textAnchor="end">
        {series[series.length - 1].date}
      </text>
    </svg>
  );
}
